/**
 * SocialProofStrip — full-width strip of trust signals shown right under
 * the hero. Drives conversion by surfacing platform-wide "everyone trusts
 * us" data points.
 *
 *   ⭐ 4.7  |  📦 10K+ orders  |  ⚡ 12 min avg  |  🌿 Freshness guaranteed
 *
 * Values are statically generated until we have a backend stats endpoint,
 * but the component is shaped to accept dynamic data later.
 */
import React from 'react';
import { css } from '../../design';

export interface ProofItem {
  icon: React.ReactNode;
  value: string;
  label: string;
}

interface Props {
  items?: ProofItem[];
}

const DEFAULTS: ProofItem[] = [
  { icon: '⭐', value: '4.7',     label: 'Average rating' },
  { icon: '📦', value: '10K+',    label: 'Orders this month' },
  { icon: '⚡', value: '~12 min', label: 'Average delivery' },
  { icon: '🌿', value: '100%',    label: 'Freshness guarantee' },
];

const SocialProofStrip: React.FC<Props> = ({ items = DEFAULTS }) => (
  <section
    aria-label="Trust signals"
    style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${items.length}, 1fr)`,
      gap: 8,
      background: css.color.surface,
      border: `1px solid ${css.color.border}`,
      borderRadius: 14,
      padding: 12,
      marginBottom: 24,
      boxShadow: css.shadow.sm,
    }}
  >
    {items.map((it, i) => (
      <div
        key={i}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: '8px 4px',
          borderRight: i < items.length - 1 ? `1px solid ${css.color.border}` : 'none',
          minWidth: 0,
        }}
      >
        <div style={{ fontSize: 22, lineHeight: 1, marginBottom: 4 }} aria-hidden>{it.icon}</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: css.color.text, letterSpacing: '-0.01em' }}>
          {it.value}
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: css.color.textMuted, lineHeight: 1.3 }}>
          {it.label}
        </div>
      </div>
    ))}
    <style>{`
      @media (max-width: 575.98px) {
        section[aria-label="Trust signals"] { grid-template-columns: repeat(2, 1fr) !important; }
        section[aria-label="Trust signals"] > div { border-right: none !important; }
      }
    `}</style>
  </section>
);

export default SocialProofStrip;

